import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'

import type { PeriodProps, StoreProps } from 'modules/attendance/components/ClockIn/types'
import calendar from 'modules/attendance/store/collections/calendar'
import periods from 'modules/attendance/store/collections/periods'
import type CalendarDay from 'modules/attendance/store/models/CalendarDay'
import Period from 'modules/attendance/store/models/Period'

export interface ShiftProject {
  value: string
  label: string
}

export enum EVENT_SHIFT_ACTION {
  ADD = 'add',
  EDIT = 'edit',
}

function monthFromProps(props: PeriodProps): moment.Moment {
  const { year, month } = props
  const date = moment([+year, +month - 1, 1])

  return date.isValid() ? date : moment()
}

class Store {
  props: StoreProps
  projectOptions: ShiftProject[] | undefined

  constructor(props: StoreProps) {
    makeObservable(this, {
      currentMonth: computed,
      currentPeriod: computed,
      state: computed,
      reviews: computed,
    })
    this.props = props
  }

  get projectNames() {
    return this.projectOptions ?? []
  }

  get projectModelRef() {
    return 'attendance-shift'
  }

  get year() {
    return this.props.year
  }

  get month() {
    return this.props.month
  }

  get employeeId() {
    return this.props.employeeId
  }

  get layout() {
    return this.props.layout
  }

  get currentMonth(): moment.Moment {
    return monthFromProps(this.props)
  }

  get currentPeriod(): Period | null | undefined {
    const year = this.currentMonth.year()
    const month = this.currentMonth.month() + 1
    const { employeeId } = this.props

    // TODO: use the new modules/attendance/store/collections/custom-periods instead of the periods but before
    // to do so, is necessary to remove from Attendance::Shift the Attendance::Period dependency
    return periods.find({
      year,
      month,
      employee_id: employeeId,
    })
  }

  fetchPeriodFromDate(date: moment.Moment): Period | null | undefined {
    const year = date.year()
    const month = date.month() + 1
    const { employeeId } = this.props

    return periods.find({
      year,
      month,
      employee_id: employeeId,
    })
  }

  get isDaily(): boolean {
    if (!hasFeature('dev_forfaitjour_in_time_management')) return false

    return !!this.currentPeriod?.isDaily
  }

  getDay(day: number): CalendarDay | null | undefined {
    return calendar.find({
      day,
    })
  }

  get state(): string {
    const period = this.currentPeriod

    return period ? period.state : Period.STATUS.in_progress
  }

  monthChanged(prevProps: PeriodProps): boolean {
    const prevMonth = monthFromProps(prevProps)

    return !this.currentMonth.isSame(prevMonth, 'month')
  }

  async fetch(dateFrom: moment.Moment, dateTo?: moment.Moment) {
    const { employeeId } = this.props
    const startOn = dateToString(dateFrom, 'backend')
    const endOn = dateTo
      ? dateToString(dateTo, 'backend')
      : dateToString(dateFrom.clone().endOf('month'), 'backend')
    const payloadData = {
      start_on: startOn,
      end_on: endOn,
    }

    await Promise.all([calendar.fetch({ data: { ...payloadData, id: employeeId } })])
  }

  hasReviewsForDate(date: moment.Moment, approvedDates?: string[] | null): boolean {
    const formattedDate = dateToString(date, 'backend')
    return !!approvedDates?.includes(formattedDate)
  }

  canDeletePeriod(): boolean {
    return this.currentPeriod?.policy.can('deletePeriod') ?? false
  }

  get reviews(): Set<string> {
    return new Set(this.currentPeriod?.attributes.reviews)
  }

  get estimatedMinutesUntilToday(): number {
    const period = this.currentPeriod

    if (!period) return 0

    return period.attributes.estimated_minutes_until_today
  }

  get estimatedRegularMinutesForRange(): number {
    const period = this.currentPeriod

    if (!period) return 0

    return period.attributes.estimated_regular_minutes
  }

  get estimatedOvertimeMinutesForRange(): number {
    const period = this.currentPeriod

    if (!period) return 0

    return period.attributes.estimated_overtime_minutes
  }

  get estimatedMinutesForRange(): number {
    const period = this.currentPeriod

    if (!period) return 0

    return period.attributes.estimated_minutes
  }

  estimatedMinutesUntilDateForMonth(targetDate: moment.Moment): number {
    const period = this.currentPeriod
    if (!period) return 0

    const month = this.currentMonth.month()
    const year = this.currentMonth.year()
    const allMinutes = period.attributes.estimated_minutes_distribution

    const endIndex =
      month === targetDate.month() && year === targetDate.year()
        ? targetDate.date()
        : allMinutes.length

    return allMinutes.slice(0, endIndex).reduce((acc: number, req: number) => acc + req, 0)
  }

  exceedsWithForgotClockOutDuration({
    duration,
    clockOutToleranceMinutes,
    overtimeRequestMinutes,
    trackedMinutes,
    estimatedMinutes,
  }: {
    duration: number
    clockOutToleranceMinutes: number
    overtimeRequestMinutes: number
    trackedMinutes: number
    estimatedMinutes: number
  }): boolean {
    const threshold = estimatedMinutes + clockOutToleranceMinutes + overtimeRequestMinutes

    return trackedMinutes + duration > threshold
  }

  get estimatedMinutesUntilTodayForMonth(): number {
    const today = moment()
    return this.estimatedMinutesUntilDateForMonth(today)
  }

  get estimatedMinutesUntilYesterdayForMonth(): number {
    const yesterday = moment().subtract(1, 'days')
    return this.estimatedMinutesUntilDateForMonth(yesterday)
  }

  isSeeingOwnPeriod(): boolean {
    return this.employeeId === this.props.currentEmployeeId
  }
}

export default Store
